'use client'

import {
    ClientAppointmentView,
    createFlexPayment,
    getClientAppointmentView,
} from '@/lib/api/client-portal/client-portal-api'
import { useCallback, useEffect, useState } from 'react'
import { MdArrowBack } from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import OrderDetailView from './order-detail-view'

/**
 * Detalhe de um procedimento na área do cliente (portal logado).
 *
 * Carrega a projeção curada `client-view` (sem valores discriminados) e delega a
 * renderização ao `OrderDetailView`, compartilhado com o link público
 * (`/p/:token`). Aqui a autorização é a sessão CLIENT; lá é CPF + token.
 */
export default function ClientAppointmentDetailPage() {
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()

    const [view, setView] = useState<ClientAppointmentView | null>(null)
    const [loading, setLoading] = useState(true)

    const load = useCallback(async () => {
        if (!id) return
        setLoading(true)
        try {
            const res = await getClientAppointmentView(parseInt(id, 10))
            if (!res.success || !res.result) {
                toast.error('Atendimento não encontrado')
                navigate('/client/appointments', { replace: true })
                return
            }
            setView(res.result)
        } catch {
            toast.error('Erro ao carregar o atendimento')
        } finally {
            setLoading(false)
        }
    }, [id, navigate])

    useEffect(() => {
        load()
    }, [load])

    const handleCreatePayment = async (
        amountCents: number,
        dueDate: string
    ): Promise<string | null> => {
        if (!id) return null
        try {
            const res = await createFlexPayment(parseInt(id, 10), {
                amount: amountCents,
                dueDate,
            })
            if (res.success && res.result?.billingId) {
                if (res.result.checkoutToken) {
                    // O OrderDetailView redireciona para o checkout.
                    return res.result.checkoutToken
                }
                // Sucesso sem token (raro): não redireciona, só atualiza.
                toast.success('Pagamento gerado. Use o link para pagar.')
                await load()
                return null
            }
            toast.error(res.error || 'Não foi possível gerar o pagamento.')
            return null
        } catch {
            toast.error('Não foi possível gerar o pagamento.')
            return null
        }
    }

    if (loading) {
        return (
            <div className="flex justify-center py-16">
                <span className="loading loading-spinner loading-lg text-secondary" />
            </div>
        )
    }

    if (!view) return null

    const shareUrl = view.publicToken
        ? `${window.location.origin}/p/${view.publicToken}`
        : null

    return (
        <OrderDetailView
            view={view}
            onCreatePayment={handleCreatePayment}
            shareUrl={shareUrl}
            headerSlot={
                <button
                    onClick={() => navigate('/client/appointments')}
                    className="btn btn-ghost btn-sm btn-circle"
                    aria-label="Voltar"
                >
                    <MdArrowBack className="text-lg" />
                </button>
            }
        />
    )
}
